// components/Email/EmailTemplates.jsx
import React, { useState, useEffect } from 'react';
import { emailTemplateService } from '../../services/emailService';
import { DocumentTextIcon, PlusIcon, PencilIcon, TrashIcon, CheckCircleIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { motion } from 'framer-motion';

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [templatesPerPage] = useState(6);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await emailTemplateService.getTemplates();
      setTemplates(data);
    } catch (error) {
      showMessage('Failed to load templates', 'error');
    }
  };

  const handleCreate = () => {
    setSelectedTemplate(null);
    setShowForm(true);
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await emailTemplateService.deleteTemplate(id);
      showMessage('Template deleted successfully', 'success');
      loadTemplates();
    } catch (error) {
      showMessage('Failed to delete template', 'error');
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  // Pagination logic
  const indexOfLastTemplate = currentPage * templatesPerPage;
  const indexOfFirstTemplate = indexOfLastTemplate - templatesPerPage;
  const currentTemplates = templates.slice(indexOfFirstTemplate, indexOfLastTemplate);
  const totalPages = Math.ceil(templates.length / templatesPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // NOTE: these keys match the backend's EmailTemplate.TemplateType values
  // (confirmed from SendSurveyController.cs: TemplateType == 1 is the Survey
  // Invitation template; SurveyReminderService.cs: TemplateType == 2 is the
  // Reminder template). The frontend previously used 0-based values
  // (0=Invitation, 1=Reminder, ...) which did not match the backend's 1-based
  // enum at all, so templates saved from this screen were never picked up by
  // the "Send Survey" / "Send Reminder" flows and admins had to guess the
  // right raw number. 3=Thank You / 4=Completion / 5=Welcome / 6=Password
  // Reset are not exercised by any backend code seen so far, so they are only
  // shifted by the same +1 to stay consistent with 1=Invitation/2=Reminder.
  const getGradientColors = (templateType) => {
    const gradients = {
      1: 'from-blue-500 to-cyan-500', // Survey Invitation
      2: 'from-orange-500 to-amber-500', // Reminder
      3: 'from-green-500 to-emerald-500', // Thank You
      4: 'from-purple-500 to-violet-500', // Completion
      5: 'from-teal-500 to-cyan-500', // Welcome
      6: 'from-red-500 to-rose-500' // Password Reset
    };
    return gradients[templateType] || 'from-orange-500 to-amber-500';
  };

  const getTemplateTypeText = (templateType) => {
    const types = {
      1: 'Survey Invitation',
      2: 'Reminder',
      3: 'Thank You',
      4: 'Completion',
      5: 'Welcome',
      6: 'Password Reset'
    };
    return types[templateType] || 'Other';
  };

  if (showForm) {
    return (
      <TemplateForm
        template={selectedTemplate}
        onSave={() => {
          setShowForm(false);
          loadTemplates();
        }}
        onCancel={() => setShowForm(false)}
      />
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 bg-gradient-to-br from-orange-50 via-white to-amber-50 p-6 rounded-2xl shadow-md">
      {/* Left Panel */}
      <div className="w-full lg:w-1/4 bg-gradient-to-br from-orange-100 via-white to-amber-100 rounded-2xl shadow-md p-6 border border-gray-100 h-fit">
        <div className="flex flex-col items-center text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl flex items-center justify-center mb-4">
            <DocumentTextIcon className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Email Templates</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            Create and manage beautiful email templates for survey invitations and communications.
          </p>
        </div>

        {/* Quick Stats */}
        <div className="mt-6 space-y-3">
          <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200">
            <span className="text-sm text-gray-600">Total Templates</span>
            <span className="font-semibold text-orange-600">{templates.length}</span>
          </div>
          <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200">
            <span className="text-sm text-gray-600">Active Templates</span>
            <span className="font-semibold text-green-600">
              {templates.filter(t => t.isActive).length}
            </span>
          </div>
          <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200">
            <span className="text-sm text-gray-600">Current Page</span>
            <span className="font-semibold text-blue-600">
              {currentPage} of {totalPages}
            </span>
          </div>
        </div>

        <button
          onClick={handleCreate}
          className="w-full mt-6 bg-gradient-to-r from-orange-500 to-amber-500 text-white py-3 rounded-lg hover:from-orange-600 hover:to-amber-600 transition-colors font-medium flex items-center justify-center gap-2"
        >
          <PlusIcon className="w-5 h-5" />
          Create Template
        </button>
      </div>

      {/* Right Content Panel */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex-1 bg-gradient-to-br from-orange-50 via-white to-amber-50 rounded-xl shadow-sm p-6 border border-orange-100"
      >
        {message.text && (
          <div className={`p-4 rounded-lg mb-6 ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-700' 
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            <div className="flex items-center">
              <CheckCircleIcon className={`w-5 h-5 mr-2 ${
                message.type === 'success' ? 'text-green-500' : 'text-red-500'
              }`} />
              {message.text}
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Email Templates</h1>
            <p className="text-gray-600">Manage your survey email templates</p>
          </div>
          <div className="text-sm text-gray-600">
            Showing {indexOfFirstTemplate + 1}-{Math.min(indexOfLastTemplate, templates.length)} of {templates.length} templates
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-6">
          {currentTemplates.map((template, index) => (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group"
            >
              {/* Gradient Header */}
              <div className={`h-2 bg-gradient-to-r ${getGradientColors(template.templateType)}`} />
              
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="font-semibold text-gray-800 text-lg group-hover:text-orange-600 transition-colors">
                    {template.name}
                  </h3>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      template.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {template.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <span className="text-xs text-gray-500 capitalize">
                      {getTemplateTypeText(template.templateType)}
                    </span>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="text-sm text-gray-600 mb-1">Subject:</div>
                  <div className="text-gray-800 font-medium text-sm line-clamp-2">
                    {template.subject}
                  </div>
                </div>

                <div className="mb-4">
                  <div className="text-sm text-gray-600 mb-1">Preview:</div>
                  <div className="text-gray-600 text-sm line-clamp-3 bg-gray-50 p-3 rounded-lg">
                    <div dangerouslySetInnerHTML={{ __html: template.body.replace(/<[^>]*>/g, '').substring(0, 100) + '...' }} />
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">
                    Updated {new Date(template.updatedDate || template.createdDate).toLocaleDateString()}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(template)}
                      className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(template.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center pt-6 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              
              {/* Page Numbers */}
              <div className="flex space-x-1">
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => handlePageChange(index + 1)}
                    className={`w-8 h-8 rounded-lg border text-sm font-medium transition-all ${
                      currentPage === index + 1
                        ? 'bg-orange-600 text-white border-orange-600 shadow-md'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Items Per Page Selector */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Show:</span>
              <select
                value={templatesPerPage}
                onChange={(e) => {
                  // You can make this dynamic if needed
                  console.log('Items per page changed to:', e.target.value);
                }}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                disabled
              >
                <option value={6}>6</option>
                <option value={12}>12</option>
                <option value={18}>18</option>
              </select>
            </div>
          </div>
        )}

        {/* Empty State */}
        {templates.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="text-center py-12"
          >
            <div className="w-24 h-24 bg-gradient-to-br from-orange-200 to-amber-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <DocumentTextIcon className="w-12 h-12 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No templates yet</h3>
            <p className="text-gray-600 mb-6 max-w-sm mx-auto">
              Create your first email template to send beautiful survey invitations
            </p>
            <button
              onClick={handleCreate}
              className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-3 rounded-lg hover:from-orange-600 hover:to-amber-600 transition-colors font-medium inline-flex items-center gap-2"
            >
              <PlusIcon className="w-5 h-5" />
              Create Template
            </button>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

// TemplateForm component remains exactly the same as your original
const TemplateForm = ({ template, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    templateType: 1,
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        subject: template.subject || '',
        body: template.body || '',
        templateType: template.templateType || 1,
        isActive: template.isActive !== undefined ? template.isActive : true
      });
    }
  }, [template]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleTextAreaChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Template name is required';
    if (!formData.subject.trim()) newErrors.subject = 'Email subject is required';
    if (!formData.body.trim()) newErrors.body = 'Email body is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      if (template) {
        await emailTemplateService.updateTemplate(template.id, formData);
      } else {
        await emailTemplateService.createTemplate(formData);
      }
      onSave();
    } catch (error) {
      setErrors({ general: error.response?.data?.message || 'Error saving template' });
    } finally {
      setLoading(false);
    }
  };

  // These must match the placeholder keys the backend actually substitutes
  // (see SendSurveyController.cs's `placeholders` dict for the Invitation
  // email, and SurveyReminderService.cs's `SendReminderEmailAsync` for the
  // Reminder email). The previous list included several keys the backend
  // never fills in (CompanyName, SurveyDescription, CurrentDate, ExpiryDate,
  // SupportEmail) and was missing ones it does fill in (CustomMessage,
  // DaysRemaining, ReminderCount) — any of those unsupported placeholders
  // left in a saved template body would go out to recipients un-substituted,
  // e.g. literally as "{CompanyName}".
  const placeholders = [
    { key: 'EmployeeName', description: "Recipient's full name" },
    { key: 'SurveyName', description: 'Survey name' },
    { key: 'SurveyLink', description: 'Link to take the survey' },
    { key: 'EstimatedTime', description: 'Estimated time to complete (minutes)' },
    { key: 'Deadline', description: 'Response deadline / survey end date' },
    { key: 'CustomMessage', description: 'Optional custom message (Invitation email only)' },
    { key: 'DaysRemaining', description: 'Days left to respond (Reminder email only)' },
    { key: 'ReminderCount', description: 'Which reminder number this is (Reminder email only)' }
  ];

  const insertPlaceholder = (placeholder) => {
    const textarea = document.querySelector('textarea[name="body"]');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newText = formData.body.substring(0, start) + `{${placeholder}}` + formData.body.substring(end);
    handleTextAreaChange('body', newText);
    textarea.focus();
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 bg-gradient-to-br from-orange-50 via-white to-amber-50 p-6 rounded-2xl shadow-md">
      {/* Left Panel */}
      <div className="w-full lg:w-1/4 bg-gradient-to-br from-orange-100 via-white to-amber-100 rounded-2xl shadow-md p-6 border border-gray-100 h-fit">
        <div className="flex flex-col items-center text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl flex items-center justify-center mb-4">
            <DocumentTextIcon className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            {template ? 'Edit Template' : 'New Template'}
          </h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            {template ? 'Update your email template' : 'Create a new email template for surveys'}
          </p>
        </div>

        {/* Available Placeholders */}
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Available Placeholders</h3>
          <div className="space-y-2">
            {placeholders.map((ph) => (
              <button
                key={ph.key}
                onClick={() => insertPlaceholder(ph.key)}
                className="w-full text-left p-3 text-xs bg-white border border-gray-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-colors"
              >
                <div className="font-mono text-orange-600">{`{${ph.key}}`}</div>
                <div className="text-gray-500 mt-1">{ph.description}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Right Form Panel */}
      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex-1 bg-gradient-to-br from-orange-50 via-white to-amber-50 rounded-xl shadow-sm p-6 border border-orange-100"
      >
        {errors.general && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
            <p className="text-red-700 text-sm">{errors.general}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Template Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Survey Invitation Template"
              className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none shadow-sm text-gray-800 transition-colors ${
                errors.name ? 'border-red-500' : ''
              }`}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Template Type
            </label>
            <select
              name="templateType"
              value={formData.templateType}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none shadow-sm bg-white"
            >
              <option value={1}>Survey Invitation</option>
              <option value={2}>Reminder</option>
              <option value={3}>Thank You</option>
              <option value={4}>Completion</option>
            </select>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Subject *
          </label>
          <input
            type="text"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            placeholder="You're invited to participate in our survey: {SurveyName}"
            required
            className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none shadow-sm text-gray-800 transition-colors ${
              errors.subject ? 'border-red-500' : ''
            }`}
          />
          {errors.subject && (
            <p className="text-red-500 text-sm mt-1">{errors.subject}</p>
          )}
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Body *
          </label>
          <textarea
            name="body"
            value={formData.body}
            onChange={(e) => handleTextAreaChange('body', e.target.value)}
            rows={12}
            placeholder="Dear {EmployeeName},&#10;&#10;You're invited to participate in our survey: {SurveyName}.&#10;&#10;Click here to take the survey: {SurveyLink}&#10;&#10;It should take about {EstimatedTime} minutes. Please respond by {Deadline}.&#10;&#10;Best regards,&#10;Great Workplace Club"
            required
            className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none shadow-sm text-gray-800 resize-none ${
              errors.body ? 'border-red-500' : ''
            }`}
          />
          {errors.body && (
            <p className="text-red-500 text-sm mt-1">{errors.body}</p>
          )}
          <p className="text-xs text-gray-500 mt-2">
            Use the placeholders from the left panel to insert dynamic content
          </p>
        </div>

        <div className="flex items-center space-x-3 mb-6 p-4 bg-gray-50 rounded-lg">
          <input
            type="checkbox"
            name="isActive"
            checked={formData.isActive}
            onChange={handleChange}
            className="w-5 h-5 text-orange-600 rounded border-gray-300 focus:ring-orange-400"
          />
          <label className="text-sm font-medium text-gray-700">Active Template</label>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
          >
            {loading ? (
              <>
                <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4"></span>
                Saving...
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-5 h-5" />
                {template ? 'Update Template' : 'Create Template'}
              </>
            )}
          </button>
        </div>
      </motion.form>
    </div>
  );
};

export default EmailTemplates;